import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import { Row, Col, Card } from 'react-bootstrap';
import { Users, Wallet, TrendingUp, Key, Clock, CreditCard, FileText, Gift, Timer, CheckCircle, Percent, CalendarDays } from 'lucide-react';
import LoadingSpinner from '../../../component/wallet/LoadingSpinner';
import CurrencyDisplay from '../../../component/wallet/CurrencyDisplay';
import { useAdmin } from '../../../Hooks/wallet/useadmin';

const StatCard = ({ title, value, icon: Icon, color = 'primary', prefix = '' }) => (
    <Card className="border-0 shadow-sm stat-card h-100">
        <Card.Body className="d-flex align-items-center gap-3">
            <div className={`stat-icon bg-${color} bg-opacity-10 text-${color} rounded-3 p-3`}>
                <Icon size={24} />
            </div>
            <div>
                <div className="text-muted small">{title}</div>
                <div className="fw-bold fs-5">
                    {prefix}{typeof value === 'number' ? value.toLocaleString() : value}
                </div>
            </div>
        </Card.Body>
    </Card>
);

const AdminDashboardPage = () => {
    const { sidebar } = useContext(AppContext);
    const { loading, fetchDashboard } = useAdmin();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await fetchDashboard();
                setStats(res?.data || null);
            } catch {
                // handled
            }
        };
        load();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading && !stats) return <LoadingSpinner fullPage />;
    if (!stats) return null;

    const ra = stats.reward_analytics || {};

    const cards = [
        { title: 'Total Users', value: stats.total_users, icon: Users, color: 'primary' },
        { title: 'Active Wallets', value: stats.active_wallets, icon: Wallet, color: 'success' },
        { title: 'Active Reward Wallets', value: ra.active_reward_wallets ?? 0, icon: Gift, color: 'warning' },
        { title: 'Total Balance', value: stats.total_balance, icon: TrendingUp, color: 'info', prefix: '₹' },
        { title: 'Transactions Today', value: stats.transactions_today, icon: Clock, color: 'primary' },
        { title: 'Credits Today', value: stats.credits_today, icon: TrendingUp, color: 'success', prefix: '₹' },
        { title: 'Debits Today', value: stats.debits_today, icon: TrendingUp, color: 'danger', prefix: '₹' },
        { title: 'Active Holds', value: stats.active_holds, icon: Key, color: 'warning' },
        { title: 'Pending Payouts', value: stats.pending_payouts, icon: CreditCard, color: 'danger' },
        { title: 'Pending Settlements', value: stats.pending_settlements, icon: FileText, color: 'info' },
    ];

    const rewardCards = [
        { title: 'Total Reward Balance', value: ra.total_reward_balance ?? 0, icon: Gift, color: 'warning', prefix: '₹' },
        { title: 'Avg Reward Balance', value: ra.avg_reward_balance ?? 0, icon: TrendingUp, color: 'info', prefix: '₹' },
        { title: 'Total Reward Credited', value: ra.total_reward_credited ?? 0, icon: TrendingUp, color: 'success', prefix: '₹' },
        { title: 'Total Reward Redeemed', value: ra.total_reward_redeemed ?? 0, icon: CheckCircle, color: 'primary', prefix: '₹' },
        { title: 'Usage Rate', value: `${ra.reward_usage_rate ?? 0}%`, icon: Percent, color: 'success' },
        { title: 'Expiring Soon', value: ra.reward_wallets_expiring_soon ?? 0, icon: Timer, color: 'danger' },
        { title: 'Expired Wallets', value: ra.expired_reward_wallets ?? 0, icon: Timer, color: 'secondary' },
        { title: 'Fully Used Wallets', value: ra.fully_used_reward_wallets ?? 0, icon: CheckCircle, color: 'success' },
        { title: 'Credits This Month', value: ra.reward_credits_this_month ?? 0, icon: CalendarDays, color: 'info', prefix: '₹' },
        { title: 'Redeemed This Month', value: ra.reward_redeemed_this_month ?? 0, icon: CalendarDays, color: 'warning', prefix: '₹' },
    ];

    return (
        <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
            <div className="role-content-main">
            <div className="sub-header-role mb-4">
                <h6 className="mt-2 role-text">Admin Dashboard</h6>
            </div>
                <div className="fade-in">
            

            <Row className="g-3">
                {cards.map((card) => (
                    <Col xs={12} sm={6} lg={4} xl={3} key={card.title}>
                        <StatCard {...card} />
                    </Col>
                ))}
            </Row>

            <div className="mt-4 mb-3 fw-semibold text-muted small text-uppercase letter-spacing-1">
                Reward Analytics
            </div>
            <Row className="g-3">
                {rewardCards.map((card) => (
                    <Col xs={12} sm={6} lg={4} xl={3} key={card.title}>
                        <StatCard {...card} />
                    </Col>
                ))}
            </Row>
        </div>
        </div>
        </div>
    );
};

export default AdminDashboardPage;
